<template><div><component-combinator component-name="DtSelectMenu" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use sparingly — only when a user needs to choose from about seven to 15 possible options, and you have limited space to display the options.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">For site navigation.</li>
<li class="d-docsite--list-element">If the list of options is very short. Use <RouteLink to="/components/radio.html" class="d-docsite--link d-link">Radio</RouteLink> instead.</li>
<li class="d-docsite--list-element">If the list of options is very long. Let users type the same information into an <RouteLink to="/components/input.html" class="d-docsite--link d-link">Input</RouteLink> that suggests possible options instead (aka Combobox).</li>
<li class="d-docsite--list-element">Avoid using the <code v-pre>multiple</code> attribute. Users often don't understand how to select multiple items from the select element (e.g. by holding down a modifier key).</li>
<li class="d-docsite--list-element">For selecting an action that takes immediate effect. A <code v-pre>select</code> is for selecting a choice that is only confirmed by a separate submit action (much like a <RouteLink to="/components/checkbox.html" class="d-docsite--link d-link">Checkbox</RouteLink>). For immediate actions consider the <RouteLink to="/components/dropdown.html" class="d-docsite--link d-link">Dropdown</RouteLink> component.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="best-practices" tabindex="-1" class="d-docsite--header-3">Best Practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Selects should be considered the &quot;UI of last resort,&quot; as users often find them confusing and difficult to use. Consider testing thoroughly with members of your target audience.</li>
<li class="d-docsite--list-element">Avoid making options in one dropdown menu change based on the input to another. Users often don't understand how selecting an item in one impacts another.</li>
<li class="d-docsite--list-element">When most users will (or should) pick a particular option, make it the default selection.</li>
<li class="d-docsite--list-element">Avoid auto-submission. Be wary of UI implications of automatically submitting upon selection or applying its value. Users may often change their choices multiple times, particularly if interacting with a form solely with keyboard. Auto-submission is also less accessible. For auto-submission consider the <RouteLink to="/components/dropdown.html" class="d-docsite--link d-link">Dropdown</RouteLink> component.</li>
</ul>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="base" tabindex="-1" class="d-docsite--header-3">Base <a class="header-anchor d-link d-docsite--link d-link" href="#base" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">A select is normally paired with a label, but there are times when it can be used without a label. Don't rely on the placeholder text as a label.</p>
<code-example source-code='&lt;dt-select-menu
  :options=&quot;[
        { value: ``, label: `Please select one` },
        { value: `1`, label: `Option 1` },
        { value: `2`, label: `Option 2` },
        { value: `3`, label: `Option 3` },
      ]&quot;
  label=&quot;Default&quot;
  :model-value=&quot;modelValue&quot;
  @update:model-value=&quot;onInput&quot;
/&gt;
&lt;dt-select-menu
  :options=&quot;[
        { value: ``, label: `Please select one` },
        { value: `1`, label: `Option 1` },
        { value: `2`, label: `Option 2` },
        { value: `3`, label: `Option 3` },
      ]&quot;
  label=&quot;Disabled&quot;
  disabled
  :model-value=&quot;modelValue&quot;
  @update:model-value=&quot;onInput&quot;
/&gt;'>
<div class="d-d-grid d-g-200 d-g-cols2">
  <example-select-menu label="Default" />
  <example-select-menu label="Disabled" disabled />
</div>
</code-example>
<h3 id="with-description-text" tabindex="-1" class="d-docsite--header-3">With Description Text <a class="header-anchor d-link d-docsite--link d-link" href="#with-description-text" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-select-menu
  :options=&quot;[
        { value: ``, label: `Please select one` },
        { value: `1`, label: `Option 1` },
        { value: `2`, label: `Option 2` },
        { value: `3`, label: `Option 3` },
      ]&quot;
  label=&quot;Label&quot;
  description=&quot;Optional description text&quot;
  :model-value=&quot;modelValue&quot;
  @update:model-value=&quot;onInput&quot;
/&gt;'>
<example-select-menu label="Label" description="Optional description text" />
</code-example>
<h3 id="with-validation-states" tabindex="-1" class="d-docsite--header-3">With Validation States <a class="header-anchor d-link d-docsite--link d-link" href="#with-validation-states" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Provides feedback to the user based on their interaction, or lack thereof, with a select.</p>
<code-example source-code='&lt;dt-select-menu
  :options=&quot;[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]&quot;
  :messages=&quot;[{ message: `Critical validation message`, type: `critical` }]&quot;
  label=&quot;Label&quot;
  :model-value=&quot;modelValue&quot;
  @update:model-value=&quot;onInput&quot;
/&gt;
&lt;dt-select-menu
  :options=&quot;[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]&quot;
  :messages=&quot;[{ message: `Positive validation message`, type: `positive` }]&quot;
  label=&quot;Label&quot;
  :model-value=&quot;modelValue&quot;
  @update:model-value=&quot;onInput&quot;
/&gt;
&lt;dt-select-menu
  :options=&quot;[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]&quot;
  :messages=&quot;[{ message: `Warning validation message`, type: `warning` }]&quot;
  label=&quot;Label&quot;
  :model-value=&quot;modelValue&quot;
  @update:model-value=&quot;onInput&quot;
/&gt;'>
<div class="d-d-grid d-g-200 d-g-cols3">
  <example-select-menu
    label="Label"
    :messages='[{"message":"Critical validation message","type":"critical"}]'
  />
  <example-select-menu
    label="Label"
    :messages='[{"message":"Positive validation message","type":"positive"}]'
  />
  <example-select-menu
    label="Label"
    :messages='[{"message":"Warning validation message","type":"warning"}]'
  />
</div>
</code-example>
<h3 id="with-validation-states-hidden" tabindex="-1" class="d-docsite--header-3">With Validation States Hidden <a class="header-anchor d-link d-docsite--link d-link" href="#with-validation-states-hidden" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-select-menu
  :options=&quot;[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]&quot;
  :messages=&quot;[{ message: `Critical validation message`, type: `critical` }]&quot;
  label=&quot;Label&quot;
  :model-value=&quot;modelValue&quot;
  @update:model-value=&quot;onInput&quot;
  :show-messages=&quot;false&quot;
/&gt;
&lt;dt-select-menu
  :options=&quot;[
    { value:``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]&quot;
  :messages=&quot;[{ message: `Positive validation message`, type: `positive` }]&quot;
  label=&quot;Label&quot;
  :model-value=&quot;modelValue&quot;
  @update:model-value=&quot;onInput&quot;
  :show-messages=&quot;false&quot;
/&gt;
&lt;dt-select-menu
  :options=&quot;[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]&quot;
  :messages=&quot;[{ message: `Warning validation message`, type: `warning` }]&quot;
  label=&quot;Label&quot;
  :model-value=&quot;modelValue&quot;
  @update:model-value=&quot;onInput&quot;
  :show-messages=&quot;false&quot;
/&gt;'>
<div class="d-d-grid d-g-200 d-g-cols3">
  <example-select-menu
    label="Label"
    :messages='[{"message":"Critical validation message","type":"critical"}]'
    :show-messages="false"
  />
  <example-select-menu
    label="Label"
    :messages='[{"message":"Positive validation message","type":"positive"}]'
    :show-messages="false"
  />
  <example-select-menu
    label="Label"
    :messages='[{"message":"Warning validation message","type":"warning"}]'
    :show-messages="false"
  />
</div>
</code-example>
<h3 id="with-slotted-label" tabindex="-1" class="d-docsite--header-3">With Slotted Label <a class="header-anchor d-link d-docsite--link d-link" href="#with-slotted-label" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-select-menu
  :options=&quot;[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]&quot;
  :model-value=&quot;modelValue&quot;
  @update:model-value=&quot;onInput&quot;
&gt;
  &lt;template #label&gt;
    &lt;div&gt;Slotted label&lt;/div&gt;
  &lt;/template&gt;
&lt;/dt-select-menu&gt;'>
<example-select-menu>
  <template #label>
    <div>Slotted label</div>
  </template>
</example-select-menu>
</code-example>
<h3 id="with-slotted-description" tabindex="-1" class="d-docsite--header-3">With Slotted Description <a class="header-anchor d-link d-docsite--link d-link" href="#with-slotted-description" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-select-menu
  :options=&quot;[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]&quot;
  :model-value=&quot;modelValue&quot;
  @update:model-value=&quot;onInput&quot;
&gt;
  &lt;template #description&gt;
    &lt;div&gt;Slotted description&lt;/div&gt;
  &lt;/template&gt;
&lt;/dt-select-menu&gt;'>
<example-select-menu>
  <template #description>
    <div>Slotted description</div>
  </template>
</example-select-menu>
</code-example>
<h3 id="with-slotted-options" tabindex="-1" class="d-docsite--header-3">With Slotted Options <a class="header-anchor d-link d-docsite--link d-link" href="#with-slotted-options" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-select-menu
  :model-value=&quot;modelValue&quot;
  @update:model-value=&quot;onInput&quot;
&gt;
  &lt;template #default&gt;
    &lt;option
      v-for=&quot;option in options&quot;
      :key=&quot;`with-slotted-options-${option.value}`&quot;
      :value=&quot;option.value&quot;
    &gt;
      {{ option.label }}
    &lt;/option&gt;
  &lt;/template&gt;
&lt;/dt-select-menu&gt;'>
<dt-select-menu
  label="With Slotted Options"
>
  <option value="">Slotted options</option>
  <option value="1">Option 1</option>
  <option value="2">Option 2</option>
  <option value="3">Option 3</option>
</dt-select-menu>
</code-example>
<h2 id="sizes" tabindex="-1" class="d-docsite--header-2">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">We offer different sizes for instances in which the interface requires a smaller or larger select. In general, though, use the base <code v-pre>300</code> (medium) size select as much as possible, especially in forms.</p>
<code-example source-code='&lt;dt-select-menu
  :options=&quot;[
    { value: ``, label: `Please select one` },
    { value: `1`, label: `Option 1` },
    { value: `2`, label: `Option 2` },
    { value: `3`, label: `Option 3` },
  ]&quot;
  label=&quot;Label&quot;
  :size=&quot;100|200|300|400|500&quot;
  :model-value=&quot;modelValue&quot;
  @update:model-value=&quot;onInput&quot;
/&gt;'>
<dt-stack gap="200">
  <example-select-menu label="Label" :size="100" />
  <example-select-menu label="Label" :size="200" />
  <example-select-menu label="Label" :size="300" />
  <example-select-menu label="Label" :size="400" />
  <example-select-menu label="Label" :size="500" />
</dt-stack>
</code-example>
<h2 id="label-size" tabindex="-1" class="d-docsite--header-2">Label size <a class="header-anchor d-link d-docsite--link d-link" href="#label-size" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The label text size is automatically derived from the component's <code v-pre>size</code> prop. Use the <code v-pre>label-size</code> prop to override this when you need a different label size independent of the select size. For example, the default label size for a <code v-pre>:size=&quot;300&quot;</code> select menu is <code v-pre>300</code>, but you can override it from <code v-pre>100</code> to <code v-pre>400</code>.</p>
<code-example source-code='&lt;dt-select-menu
  :options=&quot;options&quot;
  label=&quot;Label&quot;
  :size=&quot;100&quot;
  :label-size=&quot;100&quot;
/&gt;'>
<dt-stack gap="200">
  <example-select-menu label="Extra small label" :label-size="100" />
  <example-select-menu label="Small label" :label-size="200" />
  <example-select-menu label="Medium label (default)" :label-size="300" />
  <example-select-menu label="Large label" :label-size="400" />
</dt-stack>
</code-example>
<h2 id="label-strength" tabindex="-1" class="d-docsite--header-2">Label strength <a class="header-anchor d-link d-docsite--link d-link" href="#label-strength" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Override the label font weight independently of the label size. Valid values are <code v-pre>bold</code>, <code v-pre>semibold</code>, <code v-pre>medium</code>, and <code v-pre>normal</code>.</p>
<code-example source-code='&lt;dt-select-menu
  :options=&quot;options&quot;
  label=&quot;Label&quot;
  label-strength=&quot;bold|semibold|medium|normal&quot;
/&gt;'>
<dt-stack gap="200">
  <example-select-menu label="Bold label" label-strength="bold" />
  <example-select-menu label="Semibold label" label-strength="semibold" />
  <example-select-menu label="Medium label" label-strength="medium" />
  <example-select-menu label="Normal label" label-strength="normal" />
</dt-stack>
</code-example>
<h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Make sure the <code v-pre>label</code> <code v-pre>for</code> attribute match the select <code v-pre>id</code>.</li>
<li class="d-docsite--list-element">Avoiding removing <code v-pre>labels</code>. Labelled selects are user-friendly.</li>
<li class="d-docsite--list-element">Avoid relying on placeholder text as a substitute for a label.</li>
<li class="d-docsite--list-element">Avoid customizing the placeholder text.</li>
<li class="d-docsite--list-element">If the select is a required field, use the <code v-pre>aria-required</code> property and use the validation message for input errors.</li>
<li class="d-docsite--list-element">Select with validation errors should have <code v-pre>aria-describedby</code> with the <code v-pre>id</code> of the validation message.</li>
</ul>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="selectmenu" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="select" /></div></template>


<script setup>
  import ExampleSelectMenu from '@exampleComponents/ExampleSelectMenu.vue';
</script>